import type { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@env';
import type {
  AuthUser,
  GoogleSignInIntent,
  RegisterOptions,
  Tokens,
} from '@shared/models/auth';
import type { UserUpdate } from '@shared/models/user';
import { WINDOW } from '@tvd/cdk/browser';
import { Storage } from '@tvd/utils/storage';
import { EMPTY, catchError, of, switchMap, tap, timer } from 'rxjs';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { CONSENT_KEY, CookieConsentService } from '../cookies';
import { PostHogService } from '../posthog/posthog.service';
import { AUTH_PLATFORM_ADAPTER } from './auth-platform';
import { AuthApi } from './auth-api';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #authApi = inject(AuthApi);
  readonly #connectivity = inject(ConnectivityService);
  readonly #posthog = inject(PostHogService);
  readonly #winRef = inject(WINDOW);
  readonly #storage = inject(Storage);
  readonly #platform = inject(AUTH_PLATFORM_ADAPTER);

  readonly #token = signal('');
  readonly #refreshToken = signal(this.#storage.get('REFRESH_TOKEN') || '');
  readonly #refreshTokenExpiry = signal(
    this.#storage.get('REFRESH_TOKEN_EXPIRY') || 0,
  );

  readonly #refreshTokenResource = rxResource({
    params: this.#refreshTokenExpiry,
    stream: ({ params: expiry }) => {
      const diff = expiry - Date.now();
      // This case can only be true if user refreshes the page and the refresh token is expired
      // But it's ignored here, because we call getRefreshToken inside app initializer
      // So it's guaranteed that the refresh token is valid
      if (diff < 0) {
        return EMPTY;
      }
      return timer(diff).pipe(switchMap(() => this.getRefreshToken()));
    },
  });

  readonly token = this.#token.asReadonly();

  readonly #refreshTokenChange = effect(() => {
    this.#storage.set('REFRESH_TOKEN', this.#refreshToken());
    this.#storage.set('REFRESH_TOKEN_EXPIRY', this.#refreshTokenExpiry());
  });

  readonly loggedIn = computed(() => this.#token().length > 0);

  /** Retry token refresh when connectivity returns after an offline period */
  readonly #retryOnReconnect = effect(() => {
    const count = this.#connectivity.reconnected();
    if (count > 0 && !this.#token() && this.#refreshToken()) {
      this.getRefreshToken().subscribe();
    }
  });

  readonly #user = rxResource({
    params: computed(() => ({
      token: this.#token(),
    })),
    stream: ({ params }) =>
      timer(1).pipe(
        switchMap(() => {
          if (params.token) {
            return this.#authApi.getAuthUser().pipe(
              tap((user) => {
                this.#posthog.identify(user.id, {
                  email: user.email,
                  name: user.displayName,
                  environment: environment.production
                    ? 'production'
                    : 'development',
                });
                // Restore user preferences including locale
                this.restoreUserPreferences(user);
              }),
            );
          }
          // Offline with refresh token: let cache interceptor serve cached user data
          if (!this.#winRef.navigator.onLine && this.#refreshToken()) {
            return this.#authApi.getAuthUser().pipe(catchError(() => of(null)));
          }
          return of(null);
        }),
      ),
    defaultValue: null,
  });

  readonly user = this.#user.asReadonly();

  readonly userLoading = toObservable(this.#user.isLoading);

  login(email: string, password: string) {
    return this.#authApi
      .login(email, password)
      .pipe(tap((response) => this.#setTokens(response.tokens)));
  }

  getRefreshToken() {
    const refreshToken = this.#refreshToken();
    if (!refreshToken) {
      return EMPTY;
    }
    return this.#authApi.getRefreshToken(refreshToken).pipe(
      tap((response) => this.#setTokens(response.tokens)),
      catchError((error: HttpErrorResponse) => {
        // Network failure (status 0): keep refresh token for retry when back online
        // Auth error (401/403/etc): token is invalid, logout
        if (error.status > 0) {
          this.logout();
        }
        return EMPTY;
      }),
    );
  }

  checkEmail(email: string) {
    return this.#authApi.checkEmail(email);
  }

  registerAuth0(options: RegisterOptions) {
    return this.#authApi.register(options);
  }

  // Auth0 OTP Login - Step 1: Send OTP
  sendOTP(email: string) {
    return this.#authApi.sendOTP(email);
  }

  // Auth0 OTP Login - Step 2: Verify OTP and Login
  verifyOTP(email: string, otp: string) {
    return this.#authApi
      .verifyOTP(email, otp)
      .pipe(tap((response) => this.#setTokens(response.tokens)));
  }

  verifyRegistrationOTP(email: string, otp: string) {
    return this.#authApi
      .verifyRegistrationOTP(email, otp)
      .pipe(tap((response) => this.#setTokens(response.tokens)));
  }

  /** Google sign-in — stores tokens the same way the OTP paths do */
  signInWithGoogle(
    idToken: string,
    intent: GoogleSignInIntent,
    context?: HttpContext,
  ) {
    return this.#authApi
      .googleSignIn(idToken, intent, context)
      .pipe(tap((response) => this.#setTokens(response.tokens)));
  }

  registerOwnerAuth0(options: RegisterOptions) {
    return this.#authApi.registerOwner(options);
  }

  registerStaff(options: RegisterOptions) {
    if (!options.organisationId || !options.role) {
      return EMPTY;
    }

    return this.#authApi.inviteStaff(options);
  }

  requestPasswordReset(email: string, locale?: string) {
    return this.#authApi.requestPasswordReset(email);
  }

  // Reset password with token
  resetPassword(
    token: string,
    email: string,
    password: string,
    verified = false,
  ) {
    return this.#authApi.resetPassword(token, email, password, verified);
  }

  requestAccountDeletion(email: string, locale?: string) {
    return this.#authApi.requestAccountDeletion(email, locale);
  }

  confirmAccountDeletion(token: string, email: string) {
    return this.#authApi.confirmAccountDeletion(token, email);
  }

  requestEmailChange(newEmail: string, locale?: string) {
    return this.#authApi.requestEmailChange(newEmail, locale);
  }

  confirmEmailChange(
    token: string,
    oldEmail: string,
    newEmail: string,
    password?: string,
  ) {
    return this.#authApi.confirmEmailChange(
      token,
      oldEmail,
      newEmail,
      password,
    );
  }

  updateUser(userId: string, data: UserUpdate) {
    return this.#authApi.updateUser(userId, data);
  }

  getAuthMethods(email: string) {
    return this.#authApi.getAuthMethods(email);
  }

  enablePassword(password: string, confirmPassword: string) {
    return this.#authApi.enablePassword(password, confirmPassword);
  }

  disablePassword(currentPassword: string) {
    return this.#authApi.disablePassword(currentPassword);
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string,
  ) {
    return this.#authApi.changePassword(
      currentPassword,
      newPassword,
      confirmNewPassword,
    );
  }

  sendForgotPasswordOTP() {
    return this.#authApi.sendForgotPasswordOTP();
  }

  resetPasswordWithOTP(
    otp: string,
    newPassword: string,
    confirmNewPassword: string,
  ) {
    return this.#authApi.resetPasswordWithOTP(
      otp,
      newPassword,
      confirmNewPassword,
    );
  }

  #setTokens(tokens: Tokens) {
    this.#token.set(tokens.access_token);
    this.#refreshToken.set(tokens.refresh_token);
    // We subtract half an hour from the expiry time to give some buffer time for the refresh token to be used
    const halfHourInSeconds = 60 * 30;
    const expiry = Date.now() + (tokens.expires_in - halfHourInSeconds) * 1000;
    this.#refreshTokenExpiry.set(expiry);

    this.#platform.persistSession(tokens.refresh_token, expiry);
  }

  async restoreSession(): Promise<void> {
    const { refreshToken, expiry } = await this.#platform.restoreSession();
    if (refreshToken) {
      this.#refreshToken.set(refreshToken);
    }
    if (expiry) {
      this.#refreshTokenExpiry.set(expiry);
    }
  }

  // Logout method — revokes server session then clears local state
  logout(): void {
    // Platform-specific cleanup that needs the access token (e.g. native push
    // unregister). Must run before #setTokens clears it.
    this.#platform.beforeLogout?.();

    // Revoke the session on the backend (fire-and-forget — don't block on failure)
    this.#authApi.logout().subscribe({
      error: () =>
        console.warn('Server-side logout failed, clearing local state anyway'),
    });

    this.#setTokens({ access_token: '', refresh_token: '', expires_in: 0 });

    // Reset PostHog analytics and clear its data
    this.#posthog.reset();

    this.#storage.clear([
      'THEME_PREFERENCE',
      'ONBOARDING_TOUR',
      'SETTINGS_HUB_TOUR',
    ]);

    this.#winRef.sessionStorage.clear();

    // Clear all cookies (except consent preferences)
    CookieConsentService.clearAllCookies([CONSENT_KEY]);

    this.#platform.clearSession();
    this.#platform.redirectToLogin();
  }

  // Restore user preferences from backend data
  private restoreUserPreferences(user: AuthUser): void {
    try {
      // Restore user locale preference if set
      if (user.locale) {
        this.#storage.set('USER_LOCALE', user.locale);
      }

      // Seed staff mode if the user has staff roles and no mode is stored.
      // `roles` is staff-only (ADR 0008); UserModeService owns the full
      // derivation (incl. the customerOf facet and dual-member 'all').
      const USER_MODE_KEY = 'zyn_user_mode';
      const existingMode = this.#winRef.localStorage.getItem(USER_MODE_KEY);

      if (!existingMode && user.roles?.length) {
        this.#winRef.localStorage.setItem(USER_MODE_KEY, 'staff');
      }

      // Log successful session restoration
      console.debug('User session preferences restored', {
        userId: user.id,
        locale: user.locale,
        hasRoles: !!user.roles?.length,
      });
    } catch (error) {
      console.warn('Failed to restore user preferences:', error);
    }
  }

  // Refresh user data
  refreshUser(): void {
    this.#user.reload();
  }
}
